import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { BookOpen, HelpCircle, MessageCircle, Activity } from "lucide-react";

export default function HelpGuide() {
  return (
    <div className="space-y-6 p-6">
      {/* Header Section */}
      <div className="flex items-center justify-between">
        <div className="space-y-1">
          <h1 className="text-3xl font-bold tracking-tight" style={{ color: '#050500' }}>
            User Guide
          </h1>
          <p style={{ color: '#050500', opacity: 0.7 }}>
            Learn how to use the Saving Group management system
          </p>
        </div>
      </div>

      {/* Quick Start Guide */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <BookOpen className="h-5 w-5" />
            <span>Quick Start Guide</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-3">
              <h3 className="font-semibold">Getting Started</h3>
              <ul className="space-y-2 text-sm">
                <li>• Complete your profile setup</li>
                <li>• Join the savings group</li>
                <li>• Set up your contribution preferences</li>
                <li>• Explore the dashboard</li>
              </ul>
            </div>
            
            <div className="space-y-3">
              <h3 className="font-semibold">Making Contributions</h3>
              <ul className="space-y-2 text-sm">
                <li>• Navigate to Contributions</li>
                <li>• Click "Record Contribution"</li>
                <li>• Enter amount and payment method</li>
                <li>• Submit your contribution</li>
              </ul>
            </div>
            
            <div className="space-y-3">
              <h3 className="font-semibold">Requesting Loans</h3>
              <ul className="space-y-2 text-sm">
                <li>• Go to Loans & Payments</li>
                <li>• Click "New Loan Request"</li>
                <li>• Fill out loan application</li>
                <li>• Wait for approval</li>
              </ul>
            </div>
            
            <div className="space-y-3">
              <h3 className="font-semibold">Viewing Reports</h3>
              <ul className="space-y-2 text-sm">
                <li>• Access Reports & Analytics</li>
                <li>• Choose report type</li>
                <li>• Set date range</li>
                <li>• Download or print</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Feature Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Dashboard Features</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-start space-x-3">
              <div className="w-2 h-2 bg-blue-500 rounded-full mt-2"></div>
              <div>
                <p className="font-medium">Financial Overview</p>
                <p className="text-sm text-gray-500">View your contribution history and loan status</p>
              </div>
            </div>
            
            <div className="flex items-start space-x-3">
              <div className="w-2 h-2 bg-green-500 rounded-full mt-2"></div>
              <div>
                <p className="font-medium">Quick Actions</p>
                <p className="text-sm text-gray-500">Make contributions and loan payments</p>
              </div>
            </div>
            
            <div className="flex items-start space-x-3">
              <div className="w-2 h-2 bg-purple-500 rounded-full mt-2"></div>
              <div>
                <p className="font-medium">Notifications</p>
                <p className="text-sm text-gray-500">Stay updated with group activities</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Account Management</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-start space-x-3">
              <div className="w-2 h-2 bg-orange-500 rounded-full mt-2"></div>
              <div>
                <p className="font-medium">Profile Settings</p>
                <p className="text-sm text-gray-500">Update your personal information</p>
              </div>
            </div>
            
            <div className="flex items-start space-x-3">
              <div className="w-2 h-2 bg-red-500 rounded-full mt-2"></div>
              <div>
                <p className="font-medium">Security</p>
                <p className="text-sm text-gray-500">Change password and security settings</p>
              </div>
            </div>
            
            <div className="flex items-start space-x-3">
              <div className="w-2 h-2 bg-yellow-500 rounded-full mt-2"></div>
              <div>
                <p className="font-medium">Preferences</p>
                <p className="text-sm text-gray-500">Customize your experience</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Support Options */}
      <Card>
        <CardHeader>
          <CardTitle>Need More Help?</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Button 
              variant="outline" 
              className="h-auto p-4 flex flex-col items-center space-y-2"
            >
              <HelpCircle className="h-6 w-6" />
              <span>FAQ</span>
            </Button>
            
            <Button 
              variant="outline" 
              className="h-auto p-4 flex flex-col items-center space-y-2"
            >
              <MessageCircle className="h-6 w-6" />
              <span>Contact Support</span>
            </Button>
            
            <Button 
              variant="outline" 
              className="h-auto p-4 flex flex-col items-center space-y-2"
            >
              <Activity className="h-6 w-6" />
              <span>System Status</span>
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
